import * as React from "react";
import {
  FilterableCounts,
  FilterOptions,
  FilterQuery,
  TextParameterMap,
} from "../../../../../microservices/functions/src/types/product_catalog/catalog_api_types";
import { ResetAllButton } from "../components/reset_all_button";
import { isFilterAdjusted } from "../helpers/is_filter_adjusted";
import { FilterChangeHandler } from "../types";
import { BooleanFiltersControls } from "./filter_controls/boolean_filters_controls";
import { BrandFilterControls } from "./filter_controls/brand_filter_controls";
import { ParameterFiltersControls } from "./filter_controls/parameter_filters_controls";
import { PriceRangeFilterControls } from "./filter_controls/price_range_filter_controls";
import styles from "./filter_sidebar.module.sass";

type FilterSidebarProps = {
  filterOptions: FilterOptions;
  appliedSeoUrlFilters: { brandId: Array<number> };
  filterableCounts: FilterableCounts;
  allTextParameterValues: TextParameterMap;
  onChange: FilterChangeHandler;
  onReset: () => void;
  filter: FilterQuery;
};

export const FilterSidebar = React.memo(
  ({
    filterOptions,
    appliedSeoUrlFilters,
    filterableCounts,
    allTextParameterValues,
    onChange,
    onReset,
    filter,
  }: FilterSidebarProps): JSX.Element => (
    <div id="category-sidebar">
      <div id="product-filter" className={styles.productFilter}>
        <ResetAllButton
          isFilterAdjusted={isFilterAdjusted(filter)}
          onReset={onReset}
        />
        <form onSubmit={(event) => event.preventDefault()}>
          <BooleanFiltersControls
            filter={filter}
            onChange={onChange}
            filterableCount={filterableCounts}
          />
          {filterableCounts.price !== null && (
            <PriceRangeFilterControls
              priceRange={filterableCounts.price}
              appliedPriceFilter={filter.price}
              onChange={([gte, lte]) => {
                onChange({
                  ...filter,
                  price: { gte, lte },
                });
              }}
            />
          )}
          <BrandFilterControls
            brands={filterOptions.brands}
            landingPageFilterBrands={appliedSeoUrlFilters.brandId}
            filterableCounts={filterableCounts.brandId}
            onChange={(newFilteredBrandIds) => {
              onChange({
                ...filter,
                brandId: newFilteredBrandIds,
              });
            }}
            filteredBrands={filter.brandId}
          />
          <ParameterFiltersControls
            parameters={filterOptions.parameters}
            filteredParameters={filter.parameters}
            filteredBooleanParameters={filter.booleanParameters}
            onChange={(newParameterFilter) => {
              onChange({
                ...filter,
                parameters: newParameterFilter,
              });
            }}
            onBooleanChange={(newBooleanParameterFilter) => {
              onChange({
                ...filter,
                booleanParameters: newBooleanParameterFilter,
              });
            }}
            onRemove={(
              filterType: "parameters" | "booleanParameters",
              parameterId: number
            ) => {
              if (filterType === "parameters" && filter.parameters) {
                const withRemovedFilter = { ...filter.parameters };
                delete withRemovedFilter[parameterId];

                onChange({
                  ...filter,
                  parameters: withRemovedFilter,
                });
              } else if (
                filterType === "booleanParameters" &&
                filter.booleanParameters
              ) {
                const withRemovedFilter = { ...filter.booleanParameters };
                delete withRemovedFilter[parameterId];

                onChange({
                  ...filter,
                  booleanParameters: withRemovedFilter,
                });
              }
            }}
            allTextParameterValues={allTextParameterValues}
            numericParameterRanges={filterableCounts.numericParameters}
            textParameterOptions={filterableCounts.textParameters}
            booleanParameterOptions={filterableCounts.booleanParameters}
          />
        </form>
      </div>
    </div>
  )
);
